import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsIcon from "@mui/icons-material/Collections";
import { THEME_COLORS } from "../../../context/constants";
import { createAssetActor } from "../../../client/api";
import { useUserContext } from "../../../context/UserContext";
import { useNotification } from "../../../context/NotificationContext";
import { Asset, AssetActor } from "../../../context/types";
import AssetLibraryModal from "../../AssetLibraryModal";

interface AssetActorCreationModalProps {
  open: boolean;
  onClose: () => void;
  videoPageId: string;
  onCreated: (assetActor: AssetActor) => void;
}

const AssetActorCreationModal: React.FC<AssetActorCreationModalProps> = ({
  open,
  onClose,
  videoPageId,
  onCreated,
}) => {
  const { user, currentWorkspace } = useUserContext();
  const { addNotification } = useNotification();

  const [name, setName] = useState("");
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [creating, setCreating] = useState(false);
  const [assetLibraryOpen, setAssetLibraryOpen] = useState(false);

  const handleSelectFromLibrary = (asset: Asset) => {
    setSelectedAsset(asset);
    setAssetLibraryOpen(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      addNotification("Please enter a name", "error");
      return;
    }
    if (!selectedAsset) {
      addNotification("Please select an image from the asset library", "error");
      return;
    }
    if (!currentWorkspace || !user) return;

    setCreating(true);
    try {
      const assetActor = await createAssetActor({
        name: name.trim(),
        image_url: selectedAsset.url,
        image_asset_id: selectedAsset.assetId,
        workspace: currentWorkspace._id,
        videoPageId,
        createdBy: user._id,
      });

      onCreated(assetActor);
      addNotification(`Image actor "${name.trim()}" created`, "success");
      handleClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to create image actor";
      addNotification(msg, "error");
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setName("");
    setSelectedAsset(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", fontFamily: "Lexend, sans-serif" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: "Lexend, sans-serif",
            fontWeight: 600,
          }}
        >
          Add Image Actor
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, overflow: "visible" }}>
          {/* Name */}
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              sx: { fontFamily: "Lexend, sans-serif", fontSize: "14px" },
            }}
            InputLabelProps={{
              shrink: true,
              sx: { fontFamily: "Lexend, sans-serif", color: "#666" },
            }}
          />

          {/* Image selection */}
          {selectedAsset ? (
            <Box sx={{ position: "relative", mb: 1 }}>
              <Box
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  height: "200px",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <img
                  src={selectedAsset.url}
                  alt="Preview"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
              <IconButton
                onClick={() => setSelectedAsset(null)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ) : (
            <Box
              onClick={() => setAssetLibraryOpen(true)}
              sx={{
                border: "2px dashed #ccc",
                borderRadius: "12px",
                height: "160px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: "#fafafa",
                transition: "border-color 0.2s",
                "&:hover": { borderColor: THEME_COLORS.yellow },
              }}
            >
              <CollectionsIcon sx={{ fontSize: 36, color: "#bbb", mb: 1 }} />
              <Typography
                variant="body2"
                sx={{ color: "#666", fontFamily: "Lexend, sans-serif", fontWeight: 500 }}
              >
                Choose from Asset Library
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#aaa", fontFamily: "Lexend, sans-serif", mt: 0.5 }}
              >
                Select an image to animate
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ fontFamily: "Lexend, sans-serif" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !name.trim() || !selectedAsset}
            sx={{
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              fontFamily: "Lexend, sans-serif",
              fontWeight: 600,
              "&:hover": { backgroundColor: THEME_COLORS.darkYellow },
            }}
          >
            {creating ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asset Library Modal */}
      <AssetLibraryModal
        open={assetLibraryOpen}
        onClose={() => setAssetLibraryOpen(false)}
        mode="select"
        onSelect={handleSelectFromLibrary}
        filterType="image"
        title="Select Image for Actor"
      />
    </>
  );
};

export default AssetActorCreationModal;
